define('presentation/analytics/facades/analyticsListFacade',[
        'persistence/webSocket/webSocketApp',
        'externalDependencies/clientWebSocket',
        'presentation/common/callDetails/common/callDetailsBuilder',
        'presentation/common/callDetails/presentationObjects/callDetailsPresentationObject',
        'presentation/common/callDetails/presentationObjects/callDetailsReceptionistMessagePresentationObject',
        'presentation/common/callDetails/presentationObjects/callDetailsWebChatPresentationObject',
        'common/converters/phoneNumberFormatter',
        'common/converters/userFormatter'
    ],
    function (
        /** @type typeof import('persistence/webSocket/webSocketApp') */
        WebSocketApp,
        /** @type typeof import('externalDependencies/clientWebSocket') */
        ClientWebSocket
    ) {
        const clientWebSocket = ClientWebSocket;
        const _callDetailsSocket = clientWebSocket.forApplication('callDetails');
        const _chatDetailsSocket = clientWebSocket.forApplication('chatDetails');
        const _callRecordingSubscribersWebSocket = clientWebSocket.forApplication("callRecordingSubscribers");
        const _callRecordingSubscriptionWebSocket = clientWebSocket.forApplication("callRecordingSubscriptionDataSource");
        const _callRecordingWebSocket = clientWebSocket.forApplication("callRecording");
        const _voicemailSubscriptionWebSocket = clientWebSocket.forApplication("voicemailSubscriptionDataSource");
        const _voicemailWebSocket = clientWebSocket.forApplication("voicemail");

        const CallDetailsBuilderConstructor = require('presentation/common/callDetails/common/callDetailsBuilder');
        const CallDetailsPresentationObject = require('presentation/common/callDetails/presentationObjects/callDetailsPresentationObject');
        const CallDetailsReceptionistMessagePresentationObject = require('presentation/common/callDetails/presentationObjects/callDetailsReceptionistMessagePresentationObject');
        const CallDetailsWebChatPresentationObject = require('presentation/common/callDetails/presentationObjects/callDetailsWebChatPresentationObject');
        const _callDetailsBuilder = new CallDetailsBuilderConstructor();

        const PhoneNumberFormatterConstructor = require('common/converters/phoneNumberFormatter');
        const _phoneNumberFormatter = new PhoneNumberFormatterConstructor();

        const UserFormatterConstructor = require('common/converters/userFormatter');
        const _userFormatter = new UserFormatterConstructor();

        /** @type import('common/promises/promiseFactory') */
        let _promiseFactory = null;
        /** @type {WebSocketApp['prototype']} */
        let _socket = null;


        const _init = function (/** @type import('common/promises/promiseFactory') */promiseFactory) {
            _promiseFactory = promiseFactory;

            _socket = new WebSocketApp();
            _socket.init("analytics");
        };

        const _formatFormResponsesForDisplay = (formResponses) => {
            if (formResponses === null || formResponses === undefined) {
                return [];
            }

            return formResponses.map((response) => {
                const { formKey, formValue } = response;
                return {
                    key: formKey,
                    value: formValue.trim() ? formValue : "-"
                };
            });
        };

        const _getLocations = function(outsideNumbers) {
            return _promiseFactory.deferIndefinitely(function(promise) {
                const requestData = {
                    phoneNumbers: outsideNumbers
                };

                _socket.send("get_locations", requestData, function(results) {
                    if (results === "error") {
                        promise.reject(new Error(""));
                        return;
                    }
                    promise.resolve(results.numbersWithLocations);
                });
            });
        };

        const _getCallDetails = (callHistoryId) => {
            return _promiseFactory.deferIndefinitely((promise) => {
                const callDetailsRequest = {
                    callHistoryId: callHistoryId
                };
                _callDetailsSocket.send('getCallDetailsByCallHistoryId', callDetailsRequest, (callDetailsResult) => {
                    const callDetails = _callDetailsBuilder.formatCallDetailsForDisplay(callHistoryId, callDetailsResult);
                    const mapRequest = {
                        callerLocation: callDetailsResult.callerLocation,
                        calleeLocation: callDetailsResult.calleeLocation,
                        mapHeight: 200,
                        mapWidth: 744
                    };
                    _callDetailsSocket.send('getCallDetailsMapUrl', mapRequest, (mapResult) => {
                        if (mapResult) {
                            callDetails.mapUrl = mapResult;
                        }
                        promise.resolve(callDetails);
                    });
                });
            });
        };

        const _getChatDetails = (webChatId) => {
            return _promiseFactory.deferIndefinitely((promise) => {
                let callDetails = new CallDetailsPresentationObject();
                let receptionistMessage = new CallDetailsReceptionistMessagePresentationObject();
                let webChat = new CallDetailsWebChatPresentationObject();

                const chatDetailsRequest = {
                    webChatId: webChatId
                };

                _chatDetailsSocket.send('getChatDetailsByWebChatId', chatDetailsRequest, (chatDetailsResponse) => {
                    webChat.webChatId = chatDetailsResponse.webChatId;
                    webChat.scriptName = chatDetailsResponse.liveReceptionistScriptDisplayName;
                    webChat.employeeName = chatDetailsResponse.employeeName;
                    webChat.participantName = chatDetailsResponse.participantName;
                    webChat.participantEmailAddress = chatDetailsResponse.participantEmailAddress;
                    webChat.domain = chatDetailsResponse.domain;
                    webChat.webChatTranscript = chatDetailsResponse.webChatTranscript.map((transcriptMessage) => {
                        if (transcriptMessage.isAgent) {
                            transcriptMessage.sender = _userFormatter.formatFirstNameLastInitial(transcriptMessage.sender);
                        }
                        return transcriptMessage;
                    });

                    const messageDetailsResult = chatDetailsResponse.receptionistMessage;
                    if (messageDetailsResult) {
                        receptionistMessage.receptionistMessageId = messageDetailsResult.receptionistMessageId;
                        receptionistMessage.scriptName = messageDetailsResult.liveReceptionistScriptDisplayName;
                        receptionistMessage.interactionType = messageDetailsResult.interactionType;
                        receptionistMessage.employeeName = _userFormatter.formatFirstNameLastInitial(messageDetailsResult.employeeName);
                        receptionistMessage.formName = messageDetailsResult.formName;
                        receptionistMessage.isJunk = messageDetailsResult.isJunk;
                        receptionistMessage.junkStatus = messageDetailsResult.junkStatus;
                        receptionistMessage.participantName = messageDetailsResult.participantName;
                        receptionistMessage.participantEmailAddress = messageDetailsResult.participantEmailAddress;
                        receptionistMessage.domain = messageDetailsResult.domain;
                        receptionistMessage.webChatTranscript = messageDetailsResult.webChatTranscript.map((transcriptMessage) => {
                            if (transcriptMessage.isAgent) {
                                transcriptMessage.sender = _userFormatter.formatFirstNameLastInitial(transcriptMessage.sender);
                            }
                            return transcriptMessage;
                        });
                        receptionistMessage.formResponses = _formatFormResponsesForDisplay(messageDetailsResult.formResponses);
                        receptionistMessage.subscriberIds(messageDetailsResult.subscriberIds);
                        let smsRecipients = messageDetailsResult.smsRecipients || [];
                        let recipients = messageDetailsResult.recipients || [];
                        let formattedSmsRecipients = smsRecipients.map((smsRecipient) => {
                            return _phoneNumberFormatter.toInternationalWithParens(smsRecipient);
                        });
                        receptionistMessage.recipients = recipients.concat(formattedSmsRecipients).sort();
                        callDetails.receptionistMessage = receptionistMessage;
                    }

                    callDetails.webChat = webChat;
                    promise.resolve(callDetails);
                });
            });
        };

        const _deleteCallRecording = (callRecordingId) => {
            return _promiseFactory.defer((promise) => {
                const data = {
                    callRecordingId: callRecordingId,
                };

                _callRecordingWebSocket.send("deleteCallRecording", data, () => {
                    promise.resolve();
                });
            });
        };

        const _deleteVoicemailMessage = (voicemailMessageId) => {
            return _promiseFactory.defer((promise) => {
                const data = {
                    voicemailMessageId: voicemailMessageId,
                };

                _voicemailWebSocket.send("deleteMessage", data, () => {
                    promise.resolve();
                });
            });
        };

        const _setCallRecordingStatus = (messageId, statusId) => {
            const data = {
                callRecordingId: messageId,
                status: statusId
            };

            return _promiseFactory.defer((promise) => {
                _callRecordingWebSocket.send("setCallRecordingStatus", data, () => {
                    promise.resolve();
                });
            });
        };

        const _setVoicemailMessageStatus = (messageId, statusId) => {
            const data = {
                voicemailMessageId: messageId,
                status: statusId
            };

            return _promiseFactory.defer((promise) => {
                _voicemailWebSocket.send("setMessageStatus", data, () => {
                    promise.resolve();
                });
            });
        };

        const onVoicemailMessageStatusChanged = (callback) => {
            _voicemailSubscriptionWebSocket.subscribeToEvent("voicemailMessageStatusChanged", (eventData) => {
                const {voicemailMessageId, newStatus} = eventData;
                callback(voicemailMessageId, newStatus);
            });
        };

        const onVoicemailMessageDeleted = (callback) => {
            _voicemailSubscriptionWebSocket.subscribeToEvent("voicemailMessageDeleted", (eventData) => {
                const {voicemailMessageId} = eventData;
                callback(voicemailMessageId);
            });
        };

        const onCallRecordingStatusChanged = (callback) => {
            _callRecordingSubscriptionWebSocket.subscribeToEvent("callRecordingStatusChanged", (eventData) => {
                const {callRecordingId, newStatus} = eventData;
                callback(callRecordingId, newStatus);
            });
        };

        const onCallRecordingSubscribersChanged = (callback) => {
            _callRecordingSubscribersWebSocket.subscribeToEvent("callRecordingSubscribersChanged", (eventData) => {
                const {accountHostedNumberId, subscriberIds} = eventData;
                callback(accountHostedNumberId, subscriberIds);
            });
        };

        const onCallRecordingDeleted = (callback) => {
            _callRecordingSubscriptionWebSocket.subscribeToEvent("callRecordingDeleted", (eventData) => {
                const {callRecordingId} = eventData;
                callback(callRecordingId);
            });
        };

        return function () {
            const self = this;

            self.init = _init;
            self.deleteCallRecording = _deleteCallRecording;
            self.deleteVoicemailMessage = _deleteVoicemailMessage;
            self.getCallDetails = _getCallDetails;
            self.getChatDetails = _getChatDetails;
            self.getLocations = _getLocations;
            self.onCallRecordingDeleted = onCallRecordingDeleted;
            self.onCallRecordingStatusChanged = onCallRecordingStatusChanged;
            self.onCallRecordingSubscribersChanged = onCallRecordingSubscribersChanged;
            self.onVoicemailMessageDeleted = onVoicemailMessageDeleted;
            self.onVoicemailMessageStatusChanged = onVoicemailMessageStatusChanged;
            self.setCallRecordingStatus = _setCallRecordingStatus;
            self.setVoicemailMessageStatus = _setVoicemailMessageStatus;
        };
    }
);

